import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { usePermissionsStore } from '@/stores/permissions';
import { logout } from '@/auth/oidc';


function getInitials(name: string): string {
  const words = name.trim().split(/\s+/);
  return words
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

interface NavItemDef {
  to: string;
  label: string;
  view: string;
  icon: React.ReactNode;
  badge?: number;
}

interface NavItemProps {
  to: string;
  label: string;
  icon: React.ReactNode;
  badge?: number;
}

function SidebarNavItem({ to, label, icon, badge }: NavItemProps) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        isActive
          ? 'flex items-center gap-3 px-3 py-2.5 rounded-lg bg-td-magenta/15 text-white font-semibold text-sm border-l-[3px] border-td-magenta'
          : 'flex items-center gap-3 px-3 py-2.5 rounded-lg text-white/60 hover:text-white hover:bg-white/5 text-sm transition border-l-[3px] border-transparent hover:border-td-magenta/40'
      }
    >
      {icon}
      {label}
      {badge != null && (
        <span className="ml-auto bg-td-magenta text-white text-[11px] font-bold px-2 py-0.5 rounded-full">
          {badge}
        </span>
      )}
    </NavLink>
  );
}

function SidebarSubItem({ to, label }: { to: string; label: string }) {
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        isActive
          ? 'flex items-center gap-3 pl-8 pr-3 py-2 rounded-lg bg-td-magenta/15 text-white font-semibold text-sm border-l-[3px] border-td-magenta'
          : 'flex items-center gap-3 pl-8 pr-3 py-2 rounded-lg text-white/50 hover:text-white hover:bg-white/5 text-sm transition border-l-[3px] border-transparent hover:border-td-magenta/40'
      }
    >
      {label}
    </NavLink>
  );
}

// SidebarActivityLog removed — now a full-page view at /easykit/log

const NAV_ITEMS: NavItemDef[] = [
  {
    to: '/werven',
    label: 'Werven',
    view: 'warehouse_werven',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
      </svg>
    ),
  },
  {
    to: '/tasks',
    label: 'Taken',
    view: 'tasks',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
    ),
  },
  {
    to: '/planning',
    label: 'Planning',
    view: 'planning',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    to: '/bestanden',
    label: 'Bestanden',
    view: 'bestanden',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
      </svg>
    ),
  },
  {
    to: '/opmeting',
    label: 'Opmeting',
    view: 'opmeting',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 17.25 17.25 3 21 6.75 6.75 21 3 17.25zM7.5 12.75l1.5 1.5M10.5 9.75l1.5 1.5M13.5 6.75l1.5 1.5" />
      </svg>
    ),
  },
  {
    to: '/photos',
    label: "Foto's",
    view: 'photos_browser',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  {
    to: '/foto-controle',
    label: 'Foto Controle',
    view: 'foto_review',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    to: '/easykit-oplijster',
    label: 'EasyKit Oplijsting',
    view: 'easykit_oplijster',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
      </svg>
    ),
  },
  {
    to: '/easykit-inkooporder',
    label: 'EasyKit Inkooporder',
    view: 'easykit_inkooporder',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5A3.375 3.375 0 0010.125 2.25H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-6m0 0l-2.25 2.25M12 12l2.25 2.25" />
      </svg>
    ),
  },
  {
    to: '/materiaal-projecten',
    label: 'Materiaal Projecten',
    view: 'materiaal_projects',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
      </svg>
    ),
  },
  {
    to: '/voorraad',
    label: 'Voorraad',
    view: 'stock',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M20 13V7a2 2 0 00-1-1.73l-6-3.46a2 2 0 00-2 0l-6 3.46A2 2 0 004 7v8a2 2 0 001 1.73l6 3.46a2 2 0 002 0M3.27 6.96L12 12l8.73-5.04M12 22V12" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 19.5l2 2 3.5-3.5" />
      </svg>
    ),
  },
  {
    to: '/administratie',
    label: 'Administratie',
    view: 'administratie',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
  },
  {
    to: '/vanmarcke',
    label: 'Van Marcke',
    view: 'vanmarcke',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    to: '/groepsdata',
    label: 'Groepsdata',
    view: 'groepsdata',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 19h16M7 16V9m5 7V5m5 11v-4" />
      </svg>
    ),
  },
  {
    // Bright Analytics' key page (docs/work-items/bright-api-keys.md): the bright-api role only.
    to: '/bright',
    label: 'API keys',
    view: 'bright_api',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
      </svg>
    ),
  },
];

const ADMIN_ITEM: NavItemDef = {
  to: '/admin',
  label: 'Admin',
  view: 'admin',
  icon: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
};

const PLANNING_CALENDAR_ICON = (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const EASYKIT_TRUCK_ICON = (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
  </svg>
);

export function Sidebar() {
  const user = useAuthStore((s) => s.user);
  const canView = usePermissionsStore((s) => s.canView);
  const canFeature = usePermissionsStore((s) => s.canFeature);
  const initials = user ? getInitials(user.name) : '??';
  const [easykitExpanded, setEasykitExpanded] = useState(true);
  const [planningExpanded, setPlanningExpanded] = useState(true);
  const [adminExpanded, setAdminExpanded] = useState(true);
  const visibleItems = NAV_ITEMS.filter((item) => canView(item.view) && item.view !== 'planning');
  const showEasykit = canView('easykit_planning');
  const showAdmin = canView('admin');

  // Single-view roles (e.g. magazijnier) have no nav — hide the sidebar entirely so
  // their one screen gets the full width. Logout stays available via the Header avatar.
  const hasAnyNav = visibleItems.length > 0 || showEasykit || showAdmin || canView('planning');
  if (!hasAnyNav) return null;

  return (
    <aside className="hidden lg:flex lg:flex-col lg:w-60 bg-td-navy text-white flex-shrink-0">
      {/* Nav */}
      <nav className="flex-1 px-3 py-5 space-y-0.5">
        {visibleItems.map((item) => (
          <SidebarNavItem
            key={item.to}
            to={item.to}
            label={item.label}
            icon={item.icon}
            badge={item.badge}
          />
        ))}

        {/* Planning expandable group */}
        {canView('planning') && (
          <div className="pt-0.5">
            <button
              type="button"
              onClick={() => setPlanningExpanded(!planningExpanded)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-white/60 hover:text-white hover:bg-white/5 text-sm transition border-l-[3px] border-transparent hover:border-td-magenta/40 w-full"
            >
              {PLANNING_CALENDAR_ICON}
              <span className="flex-1 text-left">Planning</span>
              <svg
                className={`w-4 h-4 transition-transform ${planningExpanded ? 'rotate-90' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {planningExpanded && (
              <div className="space-y-0.5 mt-0.5">
                <SidebarSubItem to="/planning" label="Planning" />
                {/* The activity log reads need admin:activity_log (server-gated). The
                    frontend can't see API perms, but planning_edit is held by exactly
                    the roles that have admin:activity_log (admin + projectleider), so it
                    is the correct proxy — read-only planning viewers (warehouse,
                    administratie, oplijster*) don't get a link that 403s. */}
                {canFeature('planning_edit') && (
                  <SidebarSubItem to="/planning/log" label="Activiteitenlog" />
                )}
              </div>
            )}
          </div>
        )}

        {/* EasyKit expandable group */}
        {showEasykit && (
          <div className="pt-0.5">
            <button
              type="button"
              onClick={() => setEasykitExpanded(!easykitExpanded)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-white/60 hover:text-white hover:bg-white/5 text-sm transition border-l-[3px] border-transparent hover:border-td-magenta/40 w-full"
            >
              {EASYKIT_TRUCK_ICON}
              <span className="flex-1 text-left">EasyKit</span>
              <svg
                className={`w-4 h-4 transition-transform ${easykitExpanded ? 'rotate-90' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {easykitExpanded && (
              <div className="space-y-0.5 mt-0.5">
                <SidebarSubItem to="/easykit" label="Planning" />
                <SidebarSubItem to="/easykit/driver/37732" label="Dries Vercruyssen" />
                <SidebarSubItem to="/easykit/driver/37735" label="Guy Berckmans" />
                <SidebarSubItem to="/easykit/driver/37738" label="Olivier Coosemans" />
                <SidebarSubItem to="/easykit/photos" label="Foto's" />
                <SidebarSubItem to="/easykit/log" label="Activiteitenlog" />
              </div>
            )}
          </div>
        )}

        {/* Admin expandable group */}
        {showAdmin && (
          <div className="pt-0.5">
            <button
              type="button"
              onClick={() => setAdminExpanded(!adminExpanded)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-white/60 hover:text-white hover:bg-white/5 text-sm transition border-l-[3px] border-transparent hover:border-td-magenta/40 w-full"
            >
              {ADMIN_ITEM.icon}
              <span className="flex-1 text-left">Admin</span>
              <svg
                className={`w-4 h-4 transition-transform ${adminExpanded ? 'rotate-90' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {adminExpanded && (
              <div className="space-y-0.5 mt-0.5">
                <SidebarSubItem to="/admin" label="Rechten" />
                <SidebarSubItem to="/admin/controle" label="Controle" />
                <SidebarSubItem to="/admin/log" label="Server Console" />
                <SidebarSubItem to="/admin/onderaannemers" label="Onderaannemers" />
                <SidebarSubItem to="/admin/oa-prijzen" label="OA Prijzen" />
                <SidebarSubItem to="/admin/uitgevoerde-omzet" label="Uitgevoerde Omzet" />
                <SidebarSubItem to="/admin/bright" label="Bright API" />
              </div>
            )}
          </div>
        )}
      </nav>

      {/* User */}
      <div className="px-4 py-4 border-t border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-td-magenta rounded-full flex items-center justify-center text-xs font-bold">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate">{user?.name ?? 'Laden...'}</p>
            <p className="text-[11px] text-white/50">
              {user?.roles[0] ?? ''}
            </p>
          </div>
          {import.meta.env.VITE_SKIP_AUTH !== 'true' && (
            <button
              type="button"
              onClick={() => logout()}
              title="Uitloggen"
              className="p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </aside>
  );
}
